import React, { useState, useEffect, useMemo } from "react"
import usePersistedState from "../../hooks/usePersistedState"
import useCustomColors from "../../hooks/useCustomColors"
import usePurchaseRedirect from "../../hooks/usePurchaseRedirect"
import { createPortal } from "react-dom"
import html2canvas from "html2canvas"
import SpinWheel, { getSliceColor, toSlices } from "../shared/SpinWheel"
import ResultDisplay from "../shared/ResultDisplay"
import UserBar from "../shared/UserBar"
import DemoShowcase from "./DemoShowcase"
import DEMO_PROMPTS from "./data/demoPrompts"
import AiPrompt from "./AiPrompt"
import { MAX_CHOICES } from "./ChoicesInput"
import { hasPremiumAccess, authToggleLabel } from "../../helpers/auth"
import ChoicesTabContainer from "./ChoicesTabContainer"
import WheelActions from "./WheelActions"
import AuthForms from "../shared/AuthForms"
import SaveWheelModal from "./SaveWheelModal"
import LoadWheelModal from "./LoadWheelModal"
import FavoritesSelector from "./FavoritesSelector"
import SaveFavoriteModal from "./SaveFavoriteModal"
import LoadFavoriteModal from "./LoadFavoriteModal"
import PremiumPassModal, { FEATURES } from "../shared/PremiumPassModal"
import { AccountCrossSiteNote, PremiumCrossSiteNote } from "../shared/CrossSiteNote"
import MapModal from "./MapModal"
import { getJson, deleteJson } from "../../helpers/http"
import { API_ENDPOINTS } from "../../helpers/api"
import { safeGet, safeSet } from "../../helpers/localStorage"
import { downloadUrl } from "../../helpers/download"
import { NYP_SITE, RODG_SITE } from "../../helpers/sites"

const NYP_FEATURES = ["smart", "location", "smart_requests"].map((k) => FEATURES.find((f) => f.key === k))
const CAPTURABLE_CONTENT_ID = "capturable-content"
const CAPTURABLE_WHEEL_ID = "capturable-wheel"
const AUTH_ROOT_ID = "auth-root"
const DEMO_SHOWCASE_ID = "demo-showcase"
const SCREENSHOT_SCALE = 2

const decodeWheelState = (encoded) => {
  try {
    return JSON.parse(atob(encoded))
  } catch {
    return null
  }
}

const getShareableURL = (slices, winner, prompt) => {
  const encoded = btoa(JSON.stringify({ slices, winner, prompt }))
  const url = new URL(window.location.origin)
  url.searchParams.set("wheel", encoded)
  return url.toString()
}

const getValidChoices = (text) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)

const computeWinnerState = (slices, winner) => {
  if (!winner) return { result: null, winnerIndex: null }
  const winnerIndex = slices.findIndex((s) => s.text === winner.text)
  if (winnerIndex < 0) return { result: null, winnerIndex: null }
  return {
    result: { text: winner.text, color: slices[winnerIndex].color },
    winnerIndex,
  }
}

const NYP_COLORS_KEY = "spinwheel_customColors"
const loadNypColors = () => safeGet(NYP_COLORS_KEY, {})
const saveNypColors = (colors) => safeSet(NYP_COLORS_KEY, colors)

// Shown until the user has premium; the copy differs by whether they have an
// account at all, since only a signed-in user has a free daily premium pick.
const PremiumBanner = ({ isLoggedIn, onPremiumClick, onSignUpClick }) => {
  const premiumLink = (
    <a href="#" onClick={onPremiumClick}>
      premium
    </a>
  )

  return (
    <div className="premium-banner">
      {isLoggedIn ? (
        <>
          You get unlimited quick picks and 1 free {premiumLink} pick a day!
          <br />
          Go {premiumLink}
        </>
      ) : (
        <>
          You get unlimited quick picks for free!
          <br />
          <a href="#" onClick={onSignUpClick}>
            Sign up
          </a>{" "}
          and go {premiumLink}
        </>
      )}{" "}
      for smart AI and precise location.
    </div>
  )
}

export const parseSlices = (input, customColors = {}) => {
  const lines = getValidChoices(input).slice(0, MAX_CHOICES)
  return lines.length < 1 ? null : toSlices(lines, customColors)
}

const INACTIVE_DEMO = { active: false, choices: null, prompt: null, startComplete: false }
const activeDemo = (startComplete) => ({
  active: true,
  choices: DEMO_PROMPTS[0].choices,
  prompt: DEMO_PROMPTS[0].prompt,
  startComplete,
})

const SpinWheelApp = () => {
  const [inputValue, setInputValue] = usePersistedState("choices", "")
  const [result, setResult] = usePersistedState("result", null)
  const [displayedPrompt, setDisplayedPrompt] = usePersistedState("prompt", null)
  const [loadedWinnerIndex, setLoadedWinnerIndex] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [showAuthForms, setShowAuthForms] = useState(false)
  const [authMode, setAuthMode] = useState("login")
  const [activeModal, setActiveModal] = useState(null)
  const initialDemoActive = getValidChoices(inputValue).length < 1
  const { customColors, setCustomColors, handleColorChange, handleResetAllColors } = useCustomColors(
    loadNypColors,
    saveNypColors
  )
  const [aiTier, setAiTier] = usePersistedState("aiTier", "quick")
  const [useLocation, setUseLocation] = usePersistedState("useLocation", false)
  const [promptText, setPromptText] = usePersistedState("promptText", "")
  const [markers, setMarkers] = usePersistedState("markers", [])
  const [userCoords, setUserCoords] = usePersistedState("userCoords", null)
  const [demo, setDemo] = useState(() => (initialDemoActive ? activeDemo(true) : INACTIVE_DEMO))
  const [wheelKey, setWheelKey] = useState(0)
  const closeModal = () => setActiveModal(null)
  // Takes an optional event so it can serve both link clicks and bare callbacks.
  const openPremiumModal = (e) => {
    e?.preventDefault()
    setActiveModal("premiumPass")
  }
  const handleSignUpClick = (e) => {
    e.preventDefault()
    setShowAuthForms(true)
    setAuthMode("signup")
  }

  const refreshSession = () =>
    getJson(API_ENDPOINTS.SESSION).then(({ data }) => {
      if (data?.user) {
        setCurrentUser(data.user)
      } else if (aiTier !== "quick") {
        setAiTier("quick")
      }
    })

  const { purchaseSuccess, clearPurchaseSuccess } = usePurchaseRedirect(() => openPremiumModal())

  useEffect(() => {
    refreshSession()
    if (initialDemoActive) handleResetAllColors()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const applyWheelData = (data) => {
    setInputValue(data.slices.map((s) => s.text).join("\n"))
    setDisplayedPrompt(data.prompt || null)

    setCustomColors(
      Object.fromEntries(
        data.slices.map((s, i) => [i, s.color]).filter(([i, color]) => color && color !== getSliceColor(i))
      )
    )

    const { result, winnerIndex } = computeWinnerState(data.slices, data.winner)
    setResult(result)
    setLoadedWinnerIndex(winnerIndex)
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const wheelParam = params.get("wheel")
    if (!wheelParam) return

    const wheelData = decodeWheelState(wheelParam)
    if (!Array.isArray(wheelData?.slices)) return

    setTimeout(() => applyWheelData(wheelData), 0)
    window.history.replaceState({}, "", window.location.pathname)
  }, [])

  useEffect(() => {
    if (!result || !inputValue) return
    const slices = parseSlices(inputValue, customColors)
    if (!slices) return
    const { winnerIndex } = computeWinnerState(slices, result)
    if (winnerIndex != null) setLoadedWinnerIndex(winnerIndex)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await deleteJson(API_ENDPOINTS.LOGOUT)
    setCurrentUser(null)
    setAiTier("quick")
    setUseLocation(false)
    setMarkers([])
    setUserCoords(null)
  }

  const handleLogin = (user) => {
    setCurrentUser(user)
    setShowAuthForms(false)
  }

  const handleInputChange = (value) => {
    const oldChoices = getValidChoices(inputValue)
    const newChoices = getValidChoices(value)

    setInputValue(value)
    if (oldChoices.join("\n") === newChoices.join("\n")) return
    setResult(null)
    setDisplayedPrompt(null)
    setLoadedWinnerIndex(null)
    setMarkers([])
    setUserCoords(null)
  }

  const saveImage = async () => {
    const element = document.getElementById(CAPTURABLE_CONTENT_ID)
    if (!element) return

    // Clone and position off-screen to avoid visual jump during capture
    const clone = element.cloneNode(true)
    clone.classList.add("capturing")
    clone.style.position = "absolute"
    clone.style.left = "-9999px"
    document.body.appendChild(clone)

    const canvas = await html2canvas(clone, { scale: SCREENSHOT_SCALE })
    clone.remove()

    downloadUrl(canvas.toDataURL("image/png"), "spin-wheel.png")
  }

  const normalSlices = useMemo(() => parseSlices(inputValue, customColors), [inputValue, customColors])

  const handleShare = async () => {
    if (!normalSlices) return
    const url = getShareableURL(normalSlices, result, displayedPrompt)
    await navigator.clipboard.writeText(url)
  }

  const applyFavoriteData = (favorite) => {
    setAiTier(favorite.ai_tier || "quick")
    setPromptText(favorite.prompt || "")
    setUseLocation(favorite.use_location || false)
  }

  useEffect(() => {
    if (demo.active && normalSlices) {
      setDemo(INACTIVE_DEMO)
    } else if (!demo.active && !normalSlices) {
      setDemo(activeDemo(false))
      setWheelKey((k) => k + 1)
    }
  }, [normalSlices, demo.active]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleOpenPremium = () => openPremiumModal()
    document.addEventListener("nyp-open-premium", handleOpenPremium)
    return () => document.removeEventListener("nyp-open-premium", handleOpenPremium)
  }, [])

  const demoSlices = useMemo(
    () => (demo.active && demo.choices ? toSlices(demo.choices, customColors) : null),
    [demo, customColors]
  )
  const slices = demoSlices || normalSlices
  const activePrompt = demo.active ? demo.prompt : displayedPrompt
  const choices = useMemo(() => getValidChoices(inputValue).slice(0, MAX_CHOICES), [inputValue])
  const isLoggedIn = !!currentUser

  const capturableWheel = document.getElementById(CAPTURABLE_WHEEL_ID)
  const authRoot = document.getElementById(AUTH_ROOT_ID)
  const demoMount = document.getElementById(DEMO_SHOWCASE_ID)

  const wheelContent = (
    <div className="spin-wheel-app">
      <ResultDisplay result={result} />
      <SpinWheel
        key={wheelKey}
        slices={slices}
        onSpinStart={() => setResult(null)}
        onSpinComplete={(winner) => {
          setResult(winner)
          setLoadedWinnerIndex(null)
        }}
        winnerIndex={loadedWinnerIndex}
      />
      {activePrompt && <div className="prompt-display">{activePrompt}</div>}
      {markers.length > 0 && (
        <button
          className="btn-base btn-secondary map-btn"
          onClick={() => setActiveModal("map")}
          title="View choices on map"
        >
          View on Map
        </button>
      )}
    </div>
  )

  const authContent = (
    <div className="auth-container">
      {currentUser ? (
        <UserBar user={currentUser} onPremiumClick={openPremiumModal} onLogout={handleLogout} />
      ) : (
        <>
          <button
            className="btn-base btn-secondary choices-input-btn choices-input-btn-full"
            onClick={() => setShowAuthForms((show) => !show)}
            title={showAuthForms ? "Hide login form" : "Access your account"}
          >
            {authToggleLabel(showAuthForms, authMode)}
          </button>
          {showAuthForms && (
            <AuthForms
              onLogin={handleLogin}
              mode={authMode}
              onModeChange={setAuthMode}
              supportEmail="support@nahyoupick.com"
              crossSiteNote={<AccountCrossSiteNote primarySite={NYP_SITE} secondarySite={RODG_SITE} />}
            />
          )}
        </>
      )}
    </div>
  )

  return (
    <>
      {capturableWheel && createPortal(wheelContent, capturableWheel)}
      {authRoot && createPortal(authContent, authRoot)}
      {demo.active &&
        demoMount &&
        createPortal(
          <DemoShowcase
            isActive={demo.active}
            startComplete={demo.startComplete}
            onChoicesReady={(choices, prompt) => {
              setDemo((prev) =>
                prev.choices === choices && prev.prompt === prompt ? prev : { ...prev, choices, prompt }
              )
              setResult(null)
              setLoadedWinnerIndex(null)
              setWheelKey((k) => k + 1)
            }}
          />,
          demoMount
        )}
      {!hasPremiumAccess(currentUser) && (
        <PremiumBanner isLoggedIn={isLoggedIn} onPremiumClick={openPremiumModal} onSignUpClick={handleSignUpClick} />
      )}
      <div className="section-card">
        <div className="section-label">Smarts</div>
        <AiPrompt
          onChoicesGenerated={handleInputChange}
          onPromptUsed={setDisplayedPrompt}
          onMarkersReceived={(m, coords) => {
            setMarkers(m)
            setUserCoords(coords)
          }}
          maxChoices={MAX_CHOICES}
          aiTier={aiTier}
          prompt={promptText}
          onPromptChange={setPromptText}
          useLocation={useLocation}
          onUseLocationChange={setUseLocation}
          onAiTierChange={setAiTier}
          isLoggedIn={isLoggedIn}
          currentUser={currentUser}
          onPremiumClick={openPremiumModal}
        />
        <FavoritesSelector
          onSave={() => setActiveModal("saveFavorite")}
          onLoad={() => setActiveModal("loadFavorite")}
          isLoggedIn={isLoggedIn}
        />
      </div>
      <div className="section-card">
        <ChoicesTabContainer
          value={inputValue}
          onChange={handleInputChange}
          isLoggedIn={isLoggedIn}
          choices={choices}
          customColors={customColors}
          onColorChange={handleColorChange}
          onResetAllColors={handleResetAllColors}
        />
      </div>
      <div className="section-card">
        <WheelActions
          slices={normalSlices}
          result={result}
          displayedPrompt={displayedPrompt}
          onSaveImage={saveImage}
          canSaveImage={result !== null}
          onLoadWheel={applyWheelData}
          isLoggedIn={isLoggedIn}
          onSaveToAccount={() => setActiveModal("saveWheel")}
          onLoadFromAccount={() => setActiveModal("loadWheel")}
          onShare={handleShare}
        />
      </div>
      {activeModal === "saveWheel" && (
        <SaveWheelModal slices={normalSlices} winner={result} prompt={displayedPrompt} onClose={closeModal} />
      )}
      {activeModal === "loadWheel" && <LoadWheelModal onClose={closeModal} onLoad={applyWheelData} />}
      {activeModal === "saveFavorite" && (
        <SaveFavoriteModal aiTier={aiTier} prompt={promptText} useLocation={useLocation} onClose={closeModal} />
      )}
      {activeModal === "loadFavorite" && <LoadFavoriteModal onClose={closeModal} onLoad={applyFavoriteData} />}
      {activeModal === "map" && (
        <MapModal
          markers={markers}
          winner={result}
          userCoords={userCoords}
          slices={normalSlices}
          onClose={closeModal}
        />
      )}
      {activeModal === "premiumPass" && (
        <PremiumPassModal
          features={NYP_FEATURES}
          onClose={() => {
            closeModal()
            clearPurchaseSuccess()
            refreshSession()
          }}
          purchaseSuccess={purchaseSuccess}
          crossSiteNote={<PremiumCrossSiteNote primarySite={NYP_SITE} secondarySite={RODG_SITE} />}
        />
      )}
    </>
  )
}

export default SpinWheelApp
